import { attachTelegramTheme } from './theme';

/** Telegram Mini App integration (BUILD_PLAN §5.8): theme, back button, deep links, sign-in. */
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { start_param?: string; user?: { language_code?: string } };
  colorScheme: 'light' | 'dark';
  /** Bot API 6.1+; older clients lack them. */
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  themeParams: Record<string, string | undefined>;
  ready(): void;
  openTelegramLink?(url: string): void;
  expand(): void;
  BackButton: { show(): void; hide(): void; onClick(cb: () => void): void; offClick(cb: () => void): void };
  HapticFeedback?: {
    notificationOccurred(type: 'success' | 'error' | 'warning'): void;
    impactOccurred?(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
  };
  /** Bot API 6.9+: native "Share phone number" sheet; Telegram sends the contact to the bot. */
  requestContact?(callback?: (shared: boolean) => void): void;
  isVersionAtLeast?(version: string): boolean;
  /** Bot API 8.0+: sends a message the bot prepared (savePreparedInlineMessage) to a chat the user picks. */
  shareMessage?(preparedMessageId: string, callback?: (sent: boolean) => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

const IN_TELEGRAM = 'edil.telegram';

/**
 * Telegram opens Mini Apps with #tgWebAppData=… in the URL. That hash is gone after the app moves
 * to another page with a full load (sign-up, deep links), so the first detection is remembered for
 * the WebView's session; telegram-web-app.js then restores initData from its own session copy
 * (__telegram__initParams).
 */
export function launchedInTelegram(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.hash.includes('tgWebAppData') || window.Telegram?.WebApp?.initData) {
    try {
      sessionStorage.setItem(IN_TELEGRAM, '1');
    } catch {
      /* storage blocked: detection still works on this page */
    }
    return true;
  }
  try {
    return sessionStorage.getItem(IN_TELEGRAM) === '1' || sessionStorage.getItem('__telegram__initParams') !== null;
  } catch {
    return false;
  }
}

let loading: Promise<TelegramWebApp | null> | null = null;

/** Loads telegram-web-app.js only inside Telegram, so the web build stays light. */
export function loadTelegram(): Promise<TelegramWebApp | null> {
  if (!launchedInTelegram()) return Promise.resolve(null);
  loading ??= new Promise((resolve) => {
    if (window.Telegram?.WebApp) return resolve(init(window.Telegram.WebApp));
    const s = document.createElement('script');
    s.src = 'https://telegram.org/js/telegram-web-app.js';
    s.onload = () => resolve(window.Telegram?.WebApp ? init(window.Telegram.WebApp) : null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return loading;
}

function init(app: TelegramWebApp): TelegramWebApp {
  app.ready();
  app.expand();
  applyTheme(app);
  return app;
}

/**
 * The app keeps its own theme (dark by default, see lib/theme); Telegram's scheme only counts when
 * the player chose "Match my device". Telegram's header and background are coloured to match.
 */
export function applyTheme(app: TelegramWebApp): void {
  attachTelegramTheme(app.colorScheme, (bg) => {
    try {
      app.setHeaderColor?.(bg);
      app.setBackgroundColor?.(bg);
    } catch {
      // Clients before Bot API 6.9 accept only theme keys, not colours.
    }
  });
}

/** start_param "room_quick" → "/room?code=quick" (deep links to rooms, §5.8). */
export function deepLinkPath(startParam: string | undefined): string | null {
  const m = /^room_([a-z0-9-]{2,40})$/.exec(startParam ?? '');
  return m ? `/room/?code=${m[1]}` : null;
}
